import React, { useRef, useState } from 'react';
import { t } from '../lib/i18n';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { RentPaymentStatus } from './RentPaymentStatus';
import { paymentYears, paymentValues, paymentEmailMessage, paymentContext } from '../lib/rentPayments';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { api, errMsg, useApi } from '../lib/api';
import { money } from '../lib/constants';
import { useToast } from '../hooks/use-toast';
import { Banknote, Trash2 } from 'lucide-react';
import { PaymentVoidDialog } from './PaymentVoidDialog';

const today = () => new Date().toISOString().slice(0, 10);

export default function RentPaymentsDialog({ shop, onClose, onChanged }) {
  const { toast } = useToast();
  const { data, reload } = useApi(shop ? `/owner/shops/${shop.id}/payments` : '/owner/shops');
  const [form, setForm] = useState({ amount: '', years: '1', paidOn: today(), method: 'Cash', note: '', purpose: 'subscription' });
  const requestId = useRef(crypto.randomUUID());
  const inFlight = useRef(false);
  const [voiding, setVoiding] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { requestId.current = crypto.randomUUID(); setForm(f => ({ ...f, [k]: v })); };
  const { info, currentShop } = paymentContext(data, shop);
  const values = paymentValues(form, currentShop);
  const years = values.years;
  const amount = form.purpose === 'staff_addon' && form.amount === '' ? Number(currentShop?.staffAddonYearlyFee || 0) : values.amount;

  const submit = async (e) => {
    e.preventDefault(); if (inFlight.current) return; inFlight.current = true; setBusy(true);
    try {
      const { data: res } = await api.post(`/owner/shops/${shop.id}/payments`, { ...(form.amount === '' ? {} : { amount }), years, paidOn: form.paidOn, method: form.method, note: form.note, purpose: form.purpose, requestId: requestId.current });
      toast({ title: 'Payment recorded', description: `${shop.name} is now active until ${res.shop.activeUntil}.${paymentEmailMessage(res)}` });
      setForm({ amount: '', years: '1', paidOn: today(), method: 'Cash', note: '', purpose: 'subscription' });
      requestId.current = crypto.randomUUID();
      await reload(); onChanged?.();
    } catch (err) { toast({ title: 'Failed', description: errMsg(err), variant: 'destructive' }); }
    finally { setBusy(false); inFlight.current = false; }
  };


  return (
    <Dialog open={!!shop} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rent-payments-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Banknote className="h-5 w-5 text-emerald-600" /> Subscription payments — {shop?.name}</DialogTitle>
          <DialogDescription>Record confirmed external payments. Additional staff payments do not extend the subscription.</DialogDescription>
        </DialogHeader>

        <RentPaymentSummary info={info} shop={shop} currentShop={currentShop} />
        <RentPaymentForm form={form} set={set} submit={submit} amount={amount} years={years} disabled={busy || !info} />
        <RentPaymentHistory info={info} remove={setVoiding} />
        {voiding && <PaymentVoidDialog shopId={shop.id} payment={voiding} onClose={() => setVoiding(null)} onSaved={() => { setVoiding(null); reload(); onChanged?.(); toast({ title: 'Payment reversed. Audit record retained.' }); }} />}
      </DialogContent>
    </Dialog>
  );
}

const RentPaymentSummary = ({ info, shop, currentShop }) => <>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Cell label="Yearly fee" value={`€${money(currentShop?.yearlyFee)}`} testId="payments-yearly-fee" />
          <Cell label="Active until" value={info?.nextDue || shop?.activeUntil} testId="payments-next-due" />
          <Cell label="Total paid" value={`€${money(info?.totalPaid)}`} testId="payments-total-paid" />
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Status</div>
            <div className="mt-1" data-testid="payments-overdue">
              <RentPaymentStatus info={info} />
            </div>
          </div>
        </div>

        {currentShop && <p data-testid="payment-staff-breakdown" className="text-sm text-slate-600 break-words">Shop €{money(currentShop.baseYearlyFee)} + {currentShop.extraStaffCount} extra allowed staff slots €{money(currentShop.staffAddonYearlyFee)} = €{money(currentShop.yearlyFee)}/year{currentShop.legacyStaffPricing ? ' · existing price retained' : ''}</p>}
  </>;

const RentPaymentForm = ({ form, set, submit, amount, years, disabled }) => <form onSubmit={submit} className="grid sm:grid-cols-5 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="record-payment-form">
          <div className="sm:col-span-5"><F label="Payment purpose"><select data-testid="payment-purpose" value={form.purpose} onChange={event => set('purpose', event.target.value)} className="w-full rounded-md border border-slate-300 bg-white p-2 text-sm"><option value="subscription">Annual shop subscription</option><option value="staff_addon">Additional staff only</option></select></F></div>
          <F label="Years covered"><Input data-testid="payment-years-input" type="number" min="1" max="10" value={form.years} onChange={e => set('years', e.target.value)} className="font-mono bg-white" /></F>
          <F label="Amount (EUR)"><Input data-testid="payment-amount-input" type="number" min="0" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder={money(amount)} className="font-mono bg-white" /></F>
          <F label="Paid on"><Input data-testid="payment-date-input" type="date" value={form.paidOn} onChange={e => set('paidOn', e.target.value)} className="bg-white" /></F>
          <F label="Method">
            <Select value={form.method} onValueChange={v => set('method', v)}>
              <SelectTrigger data-testid="payment-method-select" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent data-testid="payment-method-options">{['Cash', 'Mobile Money', 'Bank Transfer', 'Other'].map(m => <SelectItem data-testid={`payment-method-${m.toLowerCase().replace(/ /g, '-')}`} key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Note"><Input data-testid="payment-note-input" value={form.note} onChange={e => set('note', e.target.value)} className="bg-white" /></F>
          <div className="sm:col-span-5 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-slate-600" data-testid="payment-summary">{form.purpose === 'staff_addon' ? t('Additional staff · No commission · No subscription extension') : t(years === 1 ? 'Recording {{amount}} for one year' : 'Recording {{amount}} for {{years}} years', { amount: `€${money(amount)}`, years })}</div>
            <Button type="submit" data-testid="payment-submit" disabled={disabled} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Banknote className="h-4 w-4 mr-2" /> Record payment</Button>
          </div>
        </form>;

const RentPaymentHistory = ({ info, remove }) => <div>
          <div className="text-sm font-semibold text-slate-800 mb-2">Payment history</div>
          <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="payments-table">
            <thead className="bg-slate-700 text-white"><tr>{['Paid on', 'Amount', 'Years', 'Covers', 'Method', 'Note', ''].map(h => <th key={h} className="text-left px-3 py-2 text-xs uppercase tracking-wide font-semibold">{h}</th>)}</tr></thead>
            <tbody>
              {(info?.payments || []).map((p, i) => (
                <tr key={p.id} className={i % 2 ? 'bg-emerald-50/40' : 'bg-white'} data-testid={`payment-row-${p.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{p.paidOn}</td>
                  <td className="px-3 py-2 font-mono font-semibold text-emerald-700" data-testid={`payment-amount-${p.id}`}>€{money(p.amount)}{p.billing && <p data-testid={`payment-billing-${p.id}`} className="font-sans font-normal text-xs text-slate-500 mt-1">{p.billing.staffLimit} staff slots · €{money(p.billing.yearlyFee)}/year</p>}</td>
                  <td className="px-3 py-2 font-mono">{p.purpose === 'staff_addon' ? '—' : paymentYears(p)}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 font-mono">{p.periodStart} → {p.periodEnd}</td>
                  <td className="px-3 py-2 text-slate-700">{p.method}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 max-w-[14rem] break-words">{p.note}{p.status === 'voided' && <div data-testid={`payment-reversal-audit-${p.id}`} className="mt-2 text-rose-700"><p>{p.voidReason}</p><p>{p.voidedByName} · {p.voidedAt?.slice(0, 10)}</p></div>}</td>
                  <td className="px-3 py-2 text-right">{p.status === 'voided' ? <span data-testid={`payment-voided-${p.id}`} className="text-rose-700 text-xs">Reversed</span> : <button data-testid={`payment-delete-${p.id}`} onClick={() => remove(p)} className="text-rose-600 hover:text-rose-700" title="Reverse payment" aria-label="Reverse payment"><Trash2 className="h-4 w-4" /></button>}</td>
                </tr>
              ))}
              {info && info.payments.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">No payments recorded yet</td></tr>}
            </tbody>
          </table></div>
        </div>;

function Cell({ label, value, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-base font-bold font-mono mt-1 text-slate-900" data-testid={testId}>{value ?? '—'}</div>
    </div>
  );
}

function F({ label, children }) {
  return <div className="space-y-1"><Label className="text-xs">{label}</Label>{children}</div>;
}
